import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { ArrowLeft, CheckCircle, CreditCard, Loader2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export const Checkout = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { cart, cartTotal, paypalConfig, createOrder, captureOrder, clearCart } = useApp();
  const lang = i18n.language;

  const [step, setStep] = useState('shipping'); // shipping, payment, complete
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [shippingAddress, setShippingAddress] = useState({
    full_name: '',
    email: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    postal_code: '',
    country: ''
  });

  const handleInputChange = (e) => {
    setShippingAddress({
      ...shippingAddress,
      [e.target.name]: e.target.value
    });
  };

  const validateShipping = () => {
    const required = ['full_name', 'email', 'address_line_1', 'city', 'postal_code', 'country'];
    return required.every(field => shippingAddress[field]?.trim());
  };

  const handleContinueToPayment = async () => {
    if (!validateShipping()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const order = await createOrder(shippingAddress);
      setOrderData(order);
      setStep('payment');
    } catch (error) {
      toast.error('Error creating order');
    } finally {
      setLoading(false);
    }
  };

  const handlePayPalApprove = async (data) => {
    setLoading(true);
    try {
      await captureOrder(orderData.order_id, data.orderID);
      setStep('complete');
      toast.success('Payment successful!');
    } catch (error) {
      toast.error('Payment failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMockPayment = async () => {
    setLoading(true);
    try {
      await captureOrder(orderData.order_id, 'mock_paypal_order');
      setStep('complete');
      toast.success('Order placed successfully!');
    } catch (error) {
      toast.error('Error processing order');
    } finally {
      setLoading(false);
    }
  };

  if (cart.items.length === 0 && step !== 'complete') {
    return (
      <div className="min-h-screen pt-20 flex flex-col items-center justify-center px-4">
        <p className="text-[#A3A3A3] text-lg mb-4">Your cart is empty</p>
        <Link to="/shop">
          <Button className="btn-primary">Go to Shop</Button>
        </Link>
      </div>
    );
  }

  // Order Complete
  if (step === 'complete') {
    return (
      <div className="min-h-screen pt-20 flex flex-col items-center justify-center px-4" data-testid="order-complete">
        <CheckCircle className="w-20 h-20 text-green-500 mb-6" />
        <h1 className="font-heading text-3xl text-white font-bold mb-4 text-center">
          {t('checkout.orderComplete')}
        </h1>
        <p className="text-[#A3A3A3] text-center max-w-md mb-8">
          {t('checkout.thankYou')}
        </p>
        <Link to="/shop">
          <Button className="btn-primary">
            {t('checkout.backToShop')}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Back Link */}
        <Link 
          to="/cart" 
          className="inline-flex items-center gap-2 text-[#A3A3A3] hover:text-[#FF0099] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Cart
        </Link>

        <h1 className="font-heading text-3xl sm:text-4xl text-white font-bold mb-8">
          {t('checkout.title')}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Shipping Form */}
            {step === 'shipping' && (
              <div className="bg-[#1A1A1A] border border-[#333333] p-6" data-testid="shipping-form">
                <h2 className="font-heading text-xl text-white font-bold mb-6">
                  {t('checkout.shipping')}
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label htmlFor="full_name" className="text-white mb-2 block">
                      {t('checkout.fullName')} *
                    </Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      value={shippingAddress.full_name}
                      onChange={handleInputChange}
                      className="bg-[#0a0a0a] border-[#333333] text-white"
                      data-testid="input-fullname"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <Label htmlFor="email" className="text-white mb-2 block">
                      {t('checkout.email')} *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={shippingAddress.email}
                      onChange={handleInputChange}
                      className="bg-[#0a0a0a] border-[#333333] text-white"
                      data-testid="input-email"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <Label htmlFor="address_line_1" className="text-white mb-2 block">
                      {t('checkout.address')} *
                    </Label>
                    <Input
                      id="address_line_1"
                      name="address_line_1"
                      value={shippingAddress.address_line_1}
                      onChange={handleInputChange}
                      className="bg-[#0a0a0a] border-[#333333] text-white"
                      data-testid="input-address"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <Label htmlFor="address_line_2" className="text-white mb-2 block">
                      {t('checkout.addressLine2')}
                    </Label>
                    <Input
                      id="address_line_2"
                      name="address_line_2"
                      value={shippingAddress.address_line_2}
                      onChange={handleInputChange}
                      className="bg-[#0a0a0a] border-[#333333] text-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="city" className="text-white mb-2 block">
                      {t('checkout.city')} *
                    </Label>
                    <Input
                      id="city"
                      name="city"
                      value={shippingAddress.city}
                      onChange={handleInputChange}
                      className="bg-[#0a0a0a] border-[#333333] text-white"
                      data-testid="input-city"
                    />
                  </div>

                  <div>
                    <Label htmlFor="postal_code" className="text-white mb-2 block">
                      {t('checkout.postalCode')} *
                    </Label>
                    <Input
                      id="postal_code"
                      name="postal_code"
                      value={shippingAddress.postal_code}
                      onChange={handleInputChange}
                      className="bg-[#0a0a0a] border-[#333333] text-white"
                      data-testid="input-postal"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <Label htmlFor="country" className="text-white mb-2 block">
                      {t('checkout.country')} *
                    </Label>
                    <Input
                      id="country"
                      name="country"
                      value={shippingAddress.country}
                      onChange={handleInputChange}
                      className="bg-[#0a0a0a] border-[#333333] text-white"
                      data-testid="input-country"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleContinueToPayment}
                  disabled={loading}
                  className="btn-primary w-full mt-6"
                  data-testid="continue-to-payment"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Continue to Payment'
                  )}
                </Button>
              </div>
            )}

            {/* Payment */}
            {step === 'payment' && (
              <div className="bg-[#1A1A1A] border border-[#333333] p-6" data-testid="payment-section">
                <h2 className="font-heading text-xl text-white font-bold mb-6">
                  {t('checkout.payWithPaypal')}
                </h2>

                {paypalConfig?.configured && paypalConfig?.client_id ? (
                  <PayPalScriptProvider options={{ 
                    clientId: paypalConfig.client_id,
                    currency: 'EUR'
                  }}>
                    <PayPalButtons
                      createOrder={(data, actions) => {
                        return actions.order.create({
                          purchase_units: [{
                            amount: {
                              value: cartTotal.toFixed(2),
                              currency_code: 'EUR'
                            }
                          }]
                        });
                      }}
                      onApprove={handlePayPalApprove}
                      onError={() => toast.error('PayPal error')}
                      style={{ layout: 'vertical', color: 'black' }}
                    />
                  </PayPalScriptProvider>
                ) : (
                  <div className="text-center">
                    <p className="text-[#A3A3A3] mb-4">
                      PayPal is not configured. You can still place a test order.
                    </p>
                    <Button
                      onClick={handleMockPayment}
                      disabled={loading}
                      className="btn-primary"
                      data-testid="mock-payment-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <CreditCard className="w-5 h-5 mr-2" />
                      )}
                      Place Order (Test Mode)
                    </Button>
                  </div>
                )}

                <button
                  onClick={() => setStep('shipping')}
                  className="text-[#A3A3A3] hover:text-white text-sm mt-4 block mx-auto"
                >
                  ← Back to shipping
                </button>
              </div>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-[#1A1A1A] border border-[#333333] p-6 sticky top-24">
              <h2 className="font-heading text-xl text-white font-bold mb-6">
                {t('checkout.orderSummary')}
              </h2>

              <div className="space-y-4 mb-6">
                {cart.items.map((item, index) => (
                  <div key={index} className="flex gap-3">
                    <img
                      src={item.product?.image_url}
                      alt={item.product?.name?.[lang]}
                      className="w-16 h-16 object-cover"
                    />
                    <div className="flex-1">
                      <p className="text-white text-sm font-bold line-clamp-1">
                        {item.product?.name?.[lang] || item.product?.name?.en}
                      </p>
                      <p className="text-[#A3A3A3] text-xs">
                        {t('cart.quantity')}: {item.quantity}
                      </p>
                      <p className="text-[#FF0099] text-sm font-bold">
                        €{((item.product?.price || 0) * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-[#333333] pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-white font-bold">{t('cart.total')}</span>
                  <span className="text-[#FF0099] font-heading font-bold text-2xl">
                    €{cartTotal.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
